// apps/build/src/lib/sloeAuthClient.ts
// The Studio's side of the Lab's ONE sign-in (SloeAuth). The Lab's BFF runs in
// the tenant app (https://tenant.sloelabs.com/auth/*): it owns the SloeAuth
// client, the callback and the sealed session cookie, which is scoped to
// .sloelabs.com — so the Studio at sloelabs.com shares it. Here we only:
//   - send the browser to the BFF's /auth/login (and back to this page),
//   - read /auth/session and /auth/data-token cross-origin WITH credentials
//     (the BFF allows exactly the Studio origins — LAB_AUTH_CORS_ORIGINS),
//   - sign out through the BFF.
// On an operator door (<slug>.sloelabs.com) the Studio bundle is served BY the
// tenant, so the same routes are same-origin there.
// Nothing secret lands in JS: the data token is a 5-minute, RLS-filtered
// SloeData token for this user.

const CONFIGURED = ((import.meta as any).env?.VITE_LAB_AUTH_ORIGIN as string | undefined)?.replace(/\/+$/, "");

/** Where the Lab's /auth/* routes live for this page. */
export function authBase(): string {
  if (typeof window === "undefined") return CONFIGURED || "https://tenant.sloelabs.com";
  const host = window.location.hostname;
  // Hosts the TENANT serves (operator doors, the tenant itself): same origin.
  const servedByTenant = /^[a-z0-9-]+\.sloelabs\.com$/.test(host) && host !== "www.sloelabs.com";
  if (servedByTenant) return "";
  return CONFIGURED || "https://tenant.sloelabs.com";
}
const cred = (): RequestCredentials => (authBase() ? "include" : "same-origin");

export type SloeSession = { user_id: string; email: string; name?: string; expires_at?: string };

let sessionPromise: Promise<SloeSession | null> | null = null;

/** The signed-in Lab user (one /auth/session round-trip per page load, shared). */
export function getSession(force = false): Promise<SloeSession | null> {
  if (!sessionPromise || force) {
    const p = (async () => {
      try {
        const r = await fetch(`${authBase()}/auth/session`, { credentials: cred(), cache: "no-store" });
        if (r.status !== 200) return null;
        const j = await r.json();
        return j?.authenticated && typeof j.user_id === "string"
          ? { user_id: j.user_id, email: String(j.email || ""), name: j.name, expires_at: j.expires_at }
          : null;
      } catch {
        return null;
      }
    })();
    sessionPromise = p;
    void p.then((s) => { if (!s && sessionPromise === p) sessionPromise = null; }); // don't pin a signed-out answer
  }
  return sessionPromise;
}

/** Navigate to SloeAuth (Google or an email code) and come back to `returnTo`. */
export function signIn(returnTo: string = typeof window !== "undefined" ? window.location.href : "/"): void {
  window.location.assign(`${authBase() || ""}/auth/login?return_to=${encodeURIComponent(returnTo)}`);
}

/** Revoke-first sign-out. false = SloeAuth could not confirm (still signed in on this device). */
export async function signOut(returnTo: string = window.location.href): Promise<boolean> {
  let r: Response;
  try {
    r = await fetch(`${authBase()}/auth/logout`, {
      method: "POST",
      credentials: cred(),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ return_to: returnTo }),
    });
  } catch {
    return false;
  }
  if (!r.ok) return false;
  clearDataToken();
  sessionPromise = null;
  let endSession: string | undefined;
  try { endSession = (await r.json())?.end_session_url; } catch { /* no body */ }
  window.location.assign(endSession || returnTo);
  return true;
}

function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (typeof navigator !== "undefined" ? (navigator as any).locks : undefined) as
    | { request(name: string, cb: () => Promise<T>): Promise<T> }
    | undefined;
  return locks ? locks.request("sloe-lab-refresh", fn) : fn();
}
const REFRESH_TIMEOUT_MS = 15_000;

let dataCache: { token: string; exp: number } | null = null;
let dataInflight: Promise<string | null> | null = null;
let dataGen = 0;

/** A fresh 5-minute SloeData token (role authenticated) — cached, single flight, cross-tab locked. */
export async function getDataToken(): Promise<string | null> {
  if (dataCache && dataCache.exp - 30_000 > Date.now()) return dataCache.token;
  if (dataInflight) return dataInflight;
  const gen = dataGen;
  const slot: { p?: Promise<string | null> } = {};
  const p = (slot.p = (async () => {
    try {
      const r = await withRefreshLock(() =>
        fetch(`${authBase()}/auth/data-token`, { credentials: cred(), cache: "no-store", signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS) }),
      );
      if (!r.ok || gen !== dataGen) {
        if (gen === dataGen) dataCache = null;
        return null;
      }
      const j = await r.json();
      if (gen !== dataGen) return null;
      dataCache = { token: j.access_token, exp: Date.now() + Number(j.expires_in ?? 300) * 1000 };
      return dataCache.token;
    } catch {
      return null;
    } finally {
      if (dataInflight === slot.p) dataInflight = null;
    }
  })());
  dataInflight = p;
  return p;
}

export function clearDataToken(): void {
  dataGen++;
  dataCache = null;
  dataInflight = null;
}

/** Authorization header for the Studio's own /api calls (llm-gateway memory, studio-handoff, evolve). */
export async function authHeader(): Promise<Record<string, string>> {
  const t = await getDataToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Base URL for Lab API routes that only the tenant serves (operator doors list, intake events). */
export function labApiBase(): string {
  return authBase();
}
